import { AssemblyGraph } from "../eom/assembly-graph.js";
import type { LabObject } from "../eom/lab-object.js";
import { VariableTable, type VariableValue } from "../eom/variable.js";

export type TrialRow = Record<string, VariableValue>;

/**
 * 引擎运行时状态：EOM 对象、变量表、关系图、操作记录、已记录数据字段。
 * Mission 的进度状态（当前 stage/step、StepRecord）由 mission-engine 自己持有，不放这里。
 */
export class PhysicsStore {
  readonly variables = new VariableTable();
  readonly relations = new AssemblyGraph();
  private readonly objects = new Map<string, LabObject>();
  private readonly actionLog = new Map<string, number>();
  private readonly recordedFields = new Set<string>();
  private readonly trialTables = new Map<string, TrialRow[]>();

  putObject(object: LabObject): void {
    this.objects.set(object.id, object);
  }

  getObject(id: string): LabObject | undefined {
    return this.objects.get(id);
  }

  /**
   * 动作按 (stepId, actionId) 记账，不是全局按 actionId 记账——避免第 1 个子实验做过
   * `pull_uniform`，后面每个子实验的同名动作门控被"免费"提前满足（同一个动作 id 会被多个
   * Stage 的 act 步骤复用，见 tech/108 §7）。
   */
  recordAction(stepId: string, actionId: string): number {
    const key = `${stepId}::${actionId}`;
    const count = (this.actionLog.get(key) ?? 0) + 1;
    this.actionLog.set(key, count);
    return count;
  }

  actionCount(stepId: string, actionId: string): number {
    return this.actionLog.get(`${stepId}::${actionId}`) ?? 0;
  }

  /** 清掉某个 act 步骤已经积累的动作计数——`returnToAct()` 回环回同一个 stepId 时必须调用，
   * 否则 `hasAllActions` 会读到第一次通过时留下的旧计数，门控立刻"免费"满足，学生不需要真的
   * 重新拉一次测力计，第二组 `frictionReading` 就会是没换条件之前的陈旧值。 */
  clearActionsForStep(stepId: string): void {
    const prefix = `${stepId}::`;
    for (const key of this.actionLog.keys()) {
      if (key.startsWith(prefix)) this.actionLog.delete(key);
    }
  }

  markRecorded(fieldId: string): void {
    this.recordedFields.add(fieldId);
  }

  isRecorded(fieldId: string): boolean {
    return this.recordedFields.has(fieldId);
  }

  /** 把一行数据快照进某个 Stage 的数据表，返回快照后的行数。 */
  appendTrial(stageId: string, row: TrialRow): number {
    const rows = this.trialTables.get(stageId) ?? [];
    rows.push({ ...row });
    this.trialTables.set(stageId, rows);
    return rows.length;
  }

  trialCount(stageId: string): number {
    return this.trialTables.get(stageId)?.length ?? 0;
  }

  trials(stageId: string): TrialRow[] {
    return [...(this.trialTables.get(stageId) ?? [])];
  }

  reset(): void {
    this.objects.clear();
    this.actionLog.clear();
    this.recordedFields.clear();
    this.trialTables.clear();
    this.variables.reset();
    this.relations.reset();
  }
}
